/**
 * Motor isométrico vetorial (SVG) usado pelos infográficos.
 * Nenhum dado é calculado aqui: apenas geometria e cor.
 */
export const ISO_COS = Math.cos(Math.PI / 6);
export const ISO_SIN = Math.sin(Math.PI / 6);

export interface IsoView {
  /** Pixels por unidade de grade. */
  scale: number;
  originX: number;
  originY: number;
}

export type Point = [number, number];

export function project(x: number, y: number, z: number, view: IsoView): Point {
  const sx = view.originX + (x - y) * ISO_COS * view.scale;
  const sy = view.originY + (x + y) * ISO_SIN * view.scale - z * view.scale;
  return [sx, sy];
}

export function pointsAttr(points: Point[]): string {
  return points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
}

function clamp(value: number, min = 0, max = 255): number {
  return Math.min(max, Math.max(min, value));
}

function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;
  return [
    parseInt(full.slice(0, 2), 16) || 0,
    parseInt(full.slice(2, 4), 16) || 0,
    parseInt(full.slice(4, 6), 16) || 0,
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((channel) => clamp(Math.round(channel)).toString(16).padStart(2, '0')).join('')}`;
}

/** factor < 1 escurece, factor > 1 clareia (mistura com branco). */
export function shade(hex: string, factor: number): string {
  const [r, g, b] = hexToRgb(hex);
  if (factor <= 1) return rgbToHex(r * factor, g * factor, b * factor);
  const t = Math.min(1, factor - 1);
  return rgbToHex(r + (255 - r) * t, g + (255 - g) * t, b + (255 - b) * t);
}

export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export interface BoxFaces {
  top: Point[];
  left: Point[];
  right: Point[];
  /** Centro da face superior, para rótulos. */
  topCenter: Point;
  /** Aresta superior mais próxima do observador (para linhas-guia). */
  frontTop: Point;
}

/** Prisma retangular sobre a plataforma: [x0,x1] x [y0,y1], altura h (unidades). */
export function box(x0: number, x1: number, y0: number, y1: number, h: number, view: IsoView): BoxFaces {
  const p = (x: number, y: number, z: number): Point => project(x, y, z, view);
  return {
    top: [p(x0, y0, h), p(x1, y0, h), p(x1, y1, h), p(x0, y1, h)],
    left: [p(x0, y1, h), p(x1, y1, h), p(x1, y1, 0), p(x0, y1, 0)],
    right: [p(x1, y0, h), p(x1, y1, h), p(x1, y1, 0), p(x1, y0, 0)],
    topCenter: p((x0 + x1) / 2, (y0 + y1) / 2, h),
    frontTop: p(x1, y1, h),
  };
}

/** Plataforma (laje) com espessura, desenhada abaixo de z = 0. */
export function slab(width: number, depth: number, thickness: number, view: IsoView): BoxFaces {
  const p = (x: number, y: number, z: number): Point => project(x, y, z, view);
  return {
    top: [p(0, 0, 0), p(width, 0, 0), p(width, depth, 0), p(0, depth, 0)],
    left: [p(0, depth, 0), p(width, depth, 0), p(width, depth, -thickness), p(0, depth, -thickness)],
    right: [p(width, 0, 0), p(width, depth, 0), p(width, depth, -thickness), p(width, 0, -thickness)],
    topCenter: p(width / 2, depth / 2, 0),
    frontTop: p(width, depth, 0),
  };
}

/**
 * Escala que faz a cena caber na área útil do SVG.
 * `maxHeight` em unidades de grade (altura máxima dos blocos).
 */
export function fitView(params: {
  svgWidth: number;
  svgHeight: number;
  gridWidth: number;
  gridDepth: number;
  maxHeight: number;
  thickness: number;
  /** Espaço reservado para rótulos externos. */
  paddingX: number;
  paddingY: number;
}): IsoView {
  const { svgWidth, svgHeight, gridWidth, gridDepth, maxHeight, thickness, paddingX, paddingY } = params;
  const projectedWidth = (gridWidth + gridDepth) * ISO_COS;
  const projectedHeight = (gridWidth + gridDepth) * ISO_SIN + maxHeight + thickness;
  const scale = Math.min(
    (svgWidth - paddingX * 2) / projectedWidth,
    (svgHeight - paddingY * 2) / projectedHeight,
  );
  // Centraliza a cena projetada na área útil.
  const sceneWidth = projectedWidth * scale;
  const offsetX = (svgWidth - sceneWidth) / 2;
  const offsetY = (svgHeight - projectedHeight * scale) / 2;
  const originX = offsetX + gridDepth * ISO_COS * scale;
  const originY = offsetY + maxHeight * scale;
  return { scale, originX, originY };
}
